/**
 * Sketchling's own motion set (SVG + CSS, no Rive/Lottie files). One component per animation
 * role of the reference app, with the same trigger moment, length and rhythm (report 02 §4.3-4.4).
 * Timings live in `ANIM_MS` so screens and docs agree. Styles: `styles/anim.css`.
 */
import { useEffect, useState } from 'react';
import { useT, type TKey } from '../lib/i18n';
import { play } from '../lib/sfx';
import { SparkGlyph } from '../components/icons';

/** Length of each animation (ms). Loops repeat at this period. */
export const ANIM_MS = {
  logoBounce: 1720,
  bigCheck: 3600,
  smallCheck: 1200,
  nextArrows: 900,
  padlockOpen: 3030,
  coinBackdrop: 9930,
  equalizer: 1800,
  splitTip: 7000,
  penAndPaper: 2080,
  loadingLine: 2400,
  testimonial: 3200,
  deferredStep: 1600,
} as const;

/* --------------------------------- loading --------------------------------- */

/** App loading mark: our pencil-star bounces and squashes (1.72 s loop). */
export function LogoBounce({ size = 72 }: { size?: number }) {
  return (
    <span className="an-logo-bounce" style={{ width: size, height: size * 1.25 }} aria-hidden>
      <span className="an-lb-mark">
        <SparkGlyph size={size} />
      </span>
      <span className="an-lb-shadow" />
    </span>
  );
}

const LOADING_KEYS: TKey[] = ['ast.loading.1', 'ast.loading.2', 'ast.loading.3', 'ast.loading.4'];
const LOADING_COLORS = ['green', 'orange', 'purple', 'yellow'] as const;

/** Loading line that changes colour state (green → orange → purple → yellow) with an outro. */
export function LoadingText() {
  const t = useT();
  const [i, setI] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setI((n) => n + 1), ANIM_MS.loadingLine);
    return () => clearInterval(iv);
  }, []);
  const k = i % LOADING_KEYS.length;
  return (
    <p key={i} className={`an-loading-text c-${LOADING_COLORS[k]}`} role="status" data-testid="loading-text">
      {t(LOADING_KEYS[k]!)
        .split(' ')
        .map((w, j) => (
          <span key={j} style={{ animationDelay: `${j * 70}ms` }}>
            {w}
          </span>
        ))}
    </p>
  );
}

/** Full loading block (logo bounce + colour line). */
export function AppLoading() {
  return (
    <div className="col center an-app-loading" data-testid="app-loading">
      <LogoBounce />
      <LoadingText />
    </div>
  );
}

/* ------------------------------- completion ticks ------------------------------- */

/** Big success check (3.6 s): ring draws, tick draws, confetti accents pop, then settles. */
export function BigCheck({ size = 140 }: { size?: number }) {
  return (
    <svg className="an-big-check" width={size} height={size} viewBox="0 0 120 120" aria-hidden>
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <g key={i} transform={`rotate(${i * 45} 60 60)`}>
          <circle className="an-bc-dot" cx="60" cy="10" r={i % 2 ? 3 : 4.5} fill={['#FFC23A', '#5AD1FF', '#FF5C7A', '#B98BFF'][i % 4]} style={{ animationDelay: `${1200 + i * 40}ms` }} />
        </g>
      ))}
      <circle className="an-bc-fill" cx="60" cy="60" r="36" fill="#3DDC84" />
      <circle className="an-bc-ring" cx="60" cy="60" r="40" fill="none" stroke="#3DDC84" strokeWidth="6" strokeLinecap="round" pathLength={1} />
      <path className="an-bc-tick" d="M42 61l12 12 24-26" fill="none" stroke="#FFFBF4" strokeWidth="9" strokeLinecap="round" strokeLinejoin="round" pathLength={1} />
    </svg>
  );
}

/** Small inline completion tick (1.2 s). */
export function SmallCheck({ size = 18 }: { size?: number }) {
  return (
    <svg className="an-small-check" width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <circle className="an-sc-disc" cx="12" cy="12" r="11" fill="#3DDC84" />
      <path className="an-sc-tick" d="M7 12.5l3.2 3.2L17 9" fill="none" stroke="#FFFBF4" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" pathLength={1} />
    </svg>
  );
}

/* --------------------------------- path cues --------------------------------- */

/** Three chevrons lighting up in turn: "this way to the next unit" (0.9 s loop). */
export function NextArrows({ className = '', dir = 'right' }: { className?: string; dir?: 'right' | 'down' }) {
  return (
    <span className={`an-next-arrows dir-${dir} ${className}`} aria-hidden>
      {[0, 1, 2].map((i) => (
        <svg key={i} viewBox="0 0 16 24" style={{ animationDelay: `${i * 150}ms` }}>
          <path d="M4 4l8 8-8 8" fill="none" stroke="currentColor" strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ))}
    </span>
  );
}

/* ----------------------------------- locks ----------------------------------- */

/** Padlock that wiggles, pops its shackle open and glows (3.03 s, plays once). */
export function PadlockOpen({ size = 64, sound = false }: { size?: number; sound?: boolean }) {
  useEffect(() => {
    if (!sound) return;
    const id = setTimeout(() => play('unlock'), 1150);
    return () => clearTimeout(id);
  }, [sound]);
  return (
    <svg className="an-padlock" width={size} height={size * 1.08} viewBox="0 0 100 108" aria-hidden>
      <circle className="an-pl-glow" cx="50" cy="66" r="44" fill="#FFC23A" />
      <path className="an-pl-shackle" d="M30 50V34a20 20 0 0 1 40 0v16" fill="none" stroke="#D8D2C8" strokeWidth="9" strokeLinecap="round" />
      <g className="an-pl-body">
        <rect x="18" y="48" width="64" height="52" rx="12" fill="#F5B700" stroke="#132630" strokeWidth="4" />
        <circle cx="50" cy="70" r="6" fill="#132630" />
        <path d="M50 74v11" stroke="#132630" strokeWidth="5" strokeLinecap="round" />
      </g>
    </svg>
  );
}

/**
 * Multicolour journey lock with a magic key. `locked` idles with a bell-like nudge; `unlock`
 * turns the key, bursts sparks and opens (magical whoosh).
 */
export function JourneyLock({ size = 72, unlock = false }: { size?: number; unlock?: boolean }) {
  useEffect(() => {
    if (unlock) play('magic');
  }, [unlock]);
  const bands = ['#FF5C7A', '#FFC23A', '#3DDC84', '#5AD1FF', '#B98BFF'];
  return (
    <svg className={`an-journey-lock${unlock ? ' unlock' : ''}`} width={size} height={size * 1.1} viewBox="0 0 100 110" aria-hidden>
      <defs>
        <clipPath id="jl-body">
          <rect x="16" y="46" width="68" height="56" rx="14" />
        </clipPath>
      </defs>
      {unlock &&
        Array.from({ length: 10 }, (_, i) => (
          <path key={i} className="an-jl-burst" d="M50 20l3 7 7 3-7 3-3 7-3-7-7-3 7-3z" fill={bands[i % 5]} style={{ ['--a' as string]: `${i * 36}deg`, animationDelay: `${900 + i * 20}ms` }} />
        ))}
      <path className="an-jl-shackle" d="M30 50V34a20 20 0 0 1 40 0v16" fill="none" stroke="#D8D2C8" strokeWidth="9" strokeLinecap="round" />
      <g className="an-jl-body">
        <g clipPath="url(#jl-body)">
          {bands.map((c, i) => (
            <rect key={c} x={16 + i * 13.6} y="46" width="13.8" height="56" fill={c} />
          ))}
        </g>
        <rect x="16" y="46" width="68" height="56" rx="14" fill="none" stroke="#132630" strokeWidth="4" />
        <circle cx="50" cy="70" r="9" fill="#FFFBF4" stroke="#132630" strokeWidth="3" />
        <g className="an-jl-key">
          <path d="M50 70v18" stroke="#132630" strokeWidth="5" strokeLinecap="round" />
        </g>
      </g>
    </svg>
  );
}

/**
 * "Draw N more to unlock": a jar that fills with paper sheets, one per drawing; when full the
 * cork pops and a little bell rings.
 */
export function ProgressJar({ have, need, size = 64 }: { have: number; need: number; size?: number }) {
  const full = have >= need;
  const frac = Math.max(0, Math.min(1, have / Math.max(1, need)));
  return (
    <svg className={`an-jar${full ? ' is-full' : ''}`} width={size} height={size * 1.15} viewBox="0 0 80 92" aria-hidden data-testid="progress-jar">
      <rect className="an-jar-cork" x="28" y="4" width="24" height="12" rx="4" fill="#C98A5A" stroke="#132630" strokeWidth="3" />
      <path d="M24 16h32v8c10 4 16 12 16 24v28a12 12 0 01-12 12H20A12 12 0 018 76V48c0-12 6-20 16-24z" fill="rgba(255,251,244,0.12)" stroke="#FFFBF4" strokeWidth="3" />
      <clipPath id="jar-in">
        <path d="M24 26c-8 4-13 11-13 22v28a9 9 0 009 9h40a9 9 0 009-9V48c0-11-5-18-13-22z" />
      </clipPath>
      <g clipPath="url(#jar-in)">
        <rect className="an-jar-fill" x="8" y={85 - 60 * frac} width="64" height={60 * frac + 2} fill="#FFC23A" opacity="0.55" />
        {Array.from({ length: Math.min(have, need) }, (_, i) => (
          <rect key={i} className="an-jar-sheet" x={18 + (i % 2) * 18} y={70 - i * 12} width="26" height="18" rx="2" fill="#FFFBF4" stroke="#132630" strokeWidth="2" transform={`rotate(${i % 2 ? 8 : -8} ${31 + (i % 2) * 18} ${79 - i * 12})`} style={{ animationDelay: `${i * 160}ms` }} />
        ))}
      </g>
      {full && <path className="an-jar-pop" d="M40 0l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" fill="#FFC23A" />}
    </svg>
  );
}

/* -------------------------------- backgrounds -------------------------------- */

/** Ambient reward background: slow rays + drifting sparkles (9.93 s loop). */
export function CoinBackdrop() {
  return (
    <div className="an-coin-backdrop" aria-hidden>
      <svg className="an-cb-rays" viewBox="-100 -100 200 200" preserveAspectRatio="xMidYMid slice">
        {Array.from({ length: 12 }, (_, i) => (
          <path key={i} d="M0 0L-9 -140L9 -140z" fill="#FFC23A" opacity="0.07" transform={`rotate(${i * 30})`} />
        ))}
      </svg>
      {Array.from({ length: 14 }, (_, i) => (
        <span key={i} className="an-cb-spark" style={{ left: `${(i * 37) % 100}%`, top: `${(i * 53) % 100}%`, animationDelay: `${-(i * 710) % ANIM_MS.coinBackdrop}ms` }} />
      ))}
    </div>
  );
}

/* ---------------------------------- media ---------------------------------- */

/** Background-music indicator: four cream bars bouncing (1.8 s loop). */
export function Equalizer({ size = 18, on = true }: { size?: number; on?: boolean }) {
  return (
    <span className={`an-eq${on ? '' : ' off'}`} style={{ width: size, height: size }} aria-hidden data-testid="equalizer">
      {[0, 1, 2, 3].map((i) => (
        <i key={i} style={{ animationDelay: `${[-300, -900, -150, -600][i]}ms` }} />
      ))}
    </span>
  );
}

/** "+10 / −10" pop shown on the player when skipping. */
export function SkipBurst({ dir, k }: { dir: 1 | -1; k: number }) {
  if (!k) return null;
  return (
    <span key={k} className={`an-skip-burst ${dir > 0 ? 'fwd' : 'back'}`} aria-hidden>
      {dir > 0 ? '+10' : '−10'}
    </span>
  );
}

/* --------------------------------- onboarding --------------------------------- */

/** Pen + paper choice art: a sheet slides in and a pencil scribbles a loop (2.08 s). */
export function PenAndPaper({ size = 120 }: { size?: number }) {
  return (
    <svg className="an-pen-paper" width={size} height={size * 0.76} viewBox="0 0 158 120" aria-hidden>
      <rect className="an-pp-sheet" x="20" y="14" width="110" height="94" rx="6" fill="#FFFBF4" stroke="#132630" strokeWidth="3" transform="rotate(-4 75 61)" />
      <path className="an-pp-line" d="M44 70c10-26 30-26 34-6s20 20 30-4" fill="none" stroke="#132630" strokeWidth="4" strokeLinecap="round" pathLength={1} />
      <g className="an-pp-pencil">
        <rect x="0" y="-6" width="56" height="12" rx="2" fill="#FFC23A" stroke="#132630" strokeWidth="2.5" />
        <path d="M0 -6l-12 6 12 6z" fill="#F6EFE3" stroke="#132630" strokeWidth="2.5" strokeLinejoin="round" />
        <rect x="48" y="-6" width="10" height="12" rx="2" fill="#FF5C7A" stroke="#132630" strokeWidth="2.5" />
      </g>
    </svg>
  );
}

/** Split-screen / small-window tip: a tablet rotates to landscape and the canvas widens (7 s loop). */
export function SplitTip({ size = 140 }: { size?: number }) {
  return (
    <svg className="an-split-tip" width={size} height={size} viewBox="0 0 140 140" aria-hidden>
      <g className="an-st-device">
        <rect x="35" y="15" width="70" height="110" rx="10" fill="#15394A" stroke="#FFFBF4" strokeWidth="4" />
        <rect className="an-st-left" x="42" y="24" width="27" height="92" rx="4" fill="#2E7A8F" />
        <rect className="an-st-right" x="72" y="24" width="26" height="92" rx="4" fill="#FFFBF4" />
        <path className="an-st-doodle" d="M78 60c4-8 10-8 12 0s-4 12-8 8" fill="none" stroke="#FF7A1A" strokeWidth="3" strokeLinecap="round" />
      </g>
      <path className="an-st-arrow" d="M118 40a50 50 0 0 1 0 60M118 100l-8-2M118 100l2-8" fill="none" stroke="#FFC23A" strokeWidth="4" strokeLinecap="round" />
    </svg>
  );
}

/* ---------------------------------- paywall ---------------------------------- */

/** Premium hero accent: avatar bubbles + a fanning colour swatch with a ringing bell. */
export function PremiumSwatch() {
  const colors = ['#FF5C7A', '#FF7A1A', '#FFC23A', '#3DDC84', '#5AD1FF', '#B98BFF'];
  return (
    <div className="an-premium" aria-hidden>
      <div className="an-pr-fan">
        {colors.map((c, i) => (
          <span key={c} style={{ background: c, ['--r' as string]: `${(i - 2.5) * 14}deg`, animationDelay: `${i * 70}ms` }} />
        ))}
      </div>
      <div className="an-pr-avatars">
        {['#FFB27A', '#B98BFF', '#5AD1FF'].map((c, i) => (
          <span key={c} style={{ background: c, animationDelay: `${300 + i * 120}ms` }}>
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="9" r="4" fill="#132630" opacity="0.8" />
              <path d="M4 22c1-5 4-7 8-7s7 2 8 7" fill="#132630" opacity="0.8" />
            </svg>
          </span>
        ))}
      </div>
    </div>
  );
}

function Critter({ kind }: { kind: 0 | 1 | 2 }) {
  // Our own three critters: a fox, an owl, a turtle (simple folded-paper shapes).
  if (kind === 0)
    return (
      <svg viewBox="0 0 48 48" width="40" height="40" aria-hidden>
        <path d="M8 10l10 8h12l10-8-4 22-12 10L12 32z" fill="#FF7A1A" stroke="#132630" strokeWidth="2.5" strokeLinejoin="round" />
        <path d="M14 30l10 6 10-6-10 10z" fill="#FFFBF4" />
        <circle cx="19" cy="25" r="2" fill="#132630" />
        <circle cx="29" cy="25" r="2" fill="#132630" />
      </svg>
    );
  if (kind === 1)
    return (
      <svg viewBox="0 0 48 48" width="40" height="40" aria-hidden>
        <path d="M10 12l6 4h16l6-4v22a12 12 0 01-12 10h-4A12 12 0 0110 34z" fill="#B98BFF" stroke="#132630" strokeWidth="2.5" strokeLinejoin="round" />
        <circle cx="19" cy="24" r="5" fill="#FFFBF4" stroke="#132630" strokeWidth="2" />
        <circle cx="29" cy="24" r="5" fill="#FFFBF4" stroke="#132630" strokeWidth="2" />
        <circle cx="19" cy="24" r="2" fill="#132630" />
        <circle cx="29" cy="24" r="2" fill="#132630" />
        <path d="M22 30l2 3 2-3z" fill="#FFC23A" />
      </svg>
    );
  return (
    <svg viewBox="0 0 48 48" width="40" height="40" aria-hidden>
      <path d="M6 32c0-10 8-16 16-16s16 6 16 16z" fill="#3DDC84" stroke="#132630" strokeWidth="2.5" />
      <path d="M14 26l8-6 8 6-8 6z" fill="none" stroke="#132630" strokeWidth="2" />
      <circle cx="41" cy="28" r="5" fill="#FFC6D9" stroke="#132630" strokeWidth="2.5" />
      <path d="M10 32v6M32 32v6" stroke="#132630" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/** Rotating testimonial cards with a bounce (one card every 3.2 s). */
export function Testimonials() {
  const t = useT();
  const [i, setI] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setI((n) => (n + 1) % 3), ANIM_MS.testimonial);
    return () => clearInterval(iv);
  }, []);
  const n = (i + 1) as 1 | 2 | 3;
  return (
    <div className="an-testimonials" data-testid="testimonials" aria-label={t('ast.testimonials')}>
      <div key={i} className="an-testi-card">
        <Critter kind={i as 0 | 1 | 2} />
        <div className="col">
          <span className="an-testi-stars" aria-hidden>
            ★★★★★
          </span>
          <q>{t(`ast.testi.${n}` as TKey).replace(/[“”]/g, '')}</q>
          <small className="muted">{t(`ast.testi.${n}by` as TKey)}</small>
        </div>
      </div>
      <div className="an-testi-dots" aria-hidden>
        {[0, 1, 2].map((k) => (
          <i key={k} className={k === i ? 'on' : ''} />
        ))}
      </div>
    </div>
  );
}

/* ---------------------------- ask-a-grown-up purchase ---------------------------- */

export type DeferredState = 'request' | 'sent' | 'pending' | 'approved' | 'denied';

/** Deferred ("ask a grown-up") purchase overlay: request → sent → pending (rotating) → result. */
export function DeferredPurchase({ state, onClose }: { state: DeferredState; onClose?: () => void }) {
  const t = useT();
  useEffect(() => {
    if (state === 'sent') play('whoosh');
    if (state === 'approved') play('unlock');
    if (state === 'denied') play('disabled');
  }, [state]);
  return (
    <div className="an-deferred" data-testid="deferred-purchase" data-state={state} role="status" onClick={onClose}>
      <div className="an-df-card col center">
        <svg className="an-df-art" viewBox="0 0 120 120" width="110" height="110" aria-hidden>
          <circle className="an-df-ring" cx="60" cy="60" r="50" fill="none" stroke="#FFC23A" strokeWidth="6" strokeDasharray="60 254" strokeLinecap="round" />
          {state === 'approved' ? (
            <path className="an-df-mark" d="M38 62l14 14 30-32" fill="none" stroke="#3DDC84" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" pathLength={1} />
          ) : state === 'denied' ? (
            <path className="an-df-mark" d="M42 42l36 36M78 42L42 78" fill="none" stroke="#FF5C7A" strokeWidth="10" strokeLinecap="round" pathLength={1} />
          ) : (
            <g className={`an-df-plane ${state}`}>
              <path d="M28 62l62-26-18 56-14-18z" fill="#FFFBF4" stroke="#132630" strokeWidth="3.5" strokeLinejoin="round" />
              <path d="M58 74l32-38" stroke="#132630" strokeWidth="3" />
            </g>
          )}
        </svg>
        <h3>{t(`ast.deferred.${state}` as TKey)}</h3>
      </div>
    </div>
  );
}

/** Auto-cycling demo of all five deferred states (asset gallery / cheats). */
export function DeferredPurchaseDemo({ onClose }: { onClose?: () => void }) {
  const order: DeferredState[] = ['request', 'sent', 'pending', 'approved', 'pending', 'denied'];
  const [i, setI] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setI((n) => (n + 1) % order.length), ANIM_MS.deferredStep);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <DeferredPurchase state={order[i]!} onClose={onClose} />;
}

/* ------------------------------ rating reactions ------------------------------ */

/** Reaction face for the star rating: grumpy (1-2) → okay (3) → delighted (4-5). */
export function RatingFace({ stars, size = 64 }: { stars: number; size?: number }) {
  const mood = stars === 0 ? 'idle' : stars <= 2 ? 'angry' : stars === 3 ? 'ok' : 'love';
  const mouth = { idle: 'M38 70q12 6 24 0', angry: 'M38 74q12-10 24 0', ok: 'M38 70h24', love: 'M34 64q16 18 32 0z' }[mood];
  return (
    <svg key={mood} className={`an-rating-face m-${mood}`} width={size} height={size} viewBox="0 0 100 100" aria-hidden data-mood={mood}>
      <circle cx="50" cy="52" r="42" fill={mood === 'angry' ? '#FF9A7A' : '#FFC23A'} stroke="#132630" strokeWidth="4" />
      {mood === 'love' ? (
        <>
          <path d="M28 44c0-6 8-6 8 0 0-6 8-6 8 0 0 5-8 10-8 10s-8-5-8-10zM56 44c0-6 8-6 8 0 0-6 8-6 8 0 0 5-8 10-8 10s-8-5-8-10z" fill="#FF5C7A" />
        </>
      ) : (
        <>
          <circle cx="36" cy="46" r="5" fill="#132630" />
          <circle cx="64" cy="46" r="5" fill="#132630" />
        </>
      )}
      {mood === 'angry' && <path d="M27 34l14 6M73 34l-14 6" stroke="#132630" strokeWidth="4" strokeLinecap="round" />}
      <path d={mouth} stroke="#132630" strokeWidth="4" fill={mood === 'love' ? '#FF5C7A' : 'none'} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}


/** Coach-mark dashed curved arrow that draws itself toward the target, then bobs. */
export function CurvedArrow({ className = '', size = 56 }: { className?: string; size?: number }) {
  return (
    <svg className={`an-curved-arrow ${className}`} width={size} height={size} viewBox="0 0 60 60" aria-hidden>
      <path className="an-ca-line" d="M8 10c22-4 40 10 40 36" fill="none" stroke="#FFFBF4" strokeWidth="3" strokeLinecap="round" strokeDasharray="5 6" />
      <path className="an-ca-head" d="M40 40l8 8 7-10" fill="none" stroke="#FFFBF4" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
